import React, { useMemo, useRef, useState } from "react";
import styles from "./ListPicker.module.scss";
import AsyncSelect from "react-select/async";
import { observer } from "mobx-react";
import { useStores } from "../../../../hooks/mobx";

type TListPickerItem = {
  value: number;
  label: string;
};

type TListPicker = {
  itemOptions: TListPickerItem[];
  getUpdatedItems?: (v: string) => void;
  onInputChange?: (v: string) => void;
  onSelect: (v: TListPickerItem) => void;
  selectedValue: TListPickerItem;
  placeholder: string;
  error?: string;
  disabled?: boolean;
  noNotFound?: boolean;
  cacheOptions?: boolean;
  isClearable?: boolean;
  customNotFound?: string;
};

const ListPicker: React.FC<TListPicker> = observer(
  ({
    itemOptions,
    placeholder,
    getUpdatedItems,
    selectedValue,
    onInputChange,
    onSelect,
    error,
    disabled,
    noNotFound,
    isClearable,
    customNotFound,
    cacheOptions = false,
  }) => {
    const SearchStyle = {
      control: (styles, state) => ({
        ...styles,
        cursor: "text",
        borderRadius: state.menuIsOpen ? "4px 4px 0 0" : "4px",
        boxShadow: "none",
        border: state.menuIsOpen
          ? "1px solid #37333F"
          : error
          ? "1px solid #CE0000"
          : "1px solid #B5B7BA",
        backgroundColor: state.isDisabled ? "#F4F5F6" : "transparent",
        "&:hover": {
          borderColor: "1px solid #B5B7BA",
          boxShadow: "none",
        },
        zIndex: 1,
      }),
      singleValue: (styles) => ({
        ...styles,
        border: "none",
        color: error ? "#CE0000" : "#131416",
        fontWeight: "normal",
        fontSize: "16px",
        lineHeight: "24px",
        width: "100%",
        padding: 0,
        margin: 0,
        zIndex: -1,
      }),
      valueContainer: (styles) => ({
        ...styles,
        padding: "16px",
      }),
      input: (styles, state) => ({
        ...styles,
        border: "none",
        color:
          state.value?.length > 0 ? "#131416" : error ? "#CE0000" : "#131416",
        fontWeight: "normal",
        fontSize: "16px",
        lineHeight: "24px",
        width: "100%",
        padding: 0,
        margin: 0,
      }),
      indicatorSeparator: () => ({
        display: "none",
      }),
      indicatorsContainer: (styles) => ({
        ...styles,
        marginRight: "8px",
        cursor: "pointer",
      }),
      placeholder: (styles, state) => ({
        ...styles,
        fontStyle: "normal",
        fontWeight: "normal",
        fontSize: "16px",
        lineHeight: "24px",
        color: "#B5B7BA",
        marginLeft: "0",
        display: state.isFocused ? "none" : "initial",
      }),
      menu: (styles) => ({
        ...styles,
        zIndex: 3,
        marginTop: 0,
        borderRadius: "0 0 4px 4px",
        boxShadow: "0px 10px 20px 0px rgba(49,8,8,0.1)",
      }),
      menuList: (styles) => ({
        ...styles,
        paddingTop: "8px",
        paddingBottom: "12px",
      }),
      groupHeading: (styles) => ({
        ...styles,
        color: "#C1C4CA",
        fontSize: "16px",
        fontWeight: "normal",
        lineHeight: "19px",
      }),
      option: (styles, state) => {
        let bacColor = "transparent";
        if (state.isFocused) bacColor = "#F6F7F8";
        if (state.isSelected) bacColor = "#F6F7F8";
        return {
          ...styles,
          backgroundColor: bacColor,
          whiteSpace: "break-spaces",
          "&:active": { backgroundColor: "#F73E3E" },
        };
      },
    };
    const { uiStore } = useStores();

    const { isMobile } = uiStore;

    const [menuIsOpen, setMenuOpened] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const selectRef = useRef<AsyncSelect>(undefined);

    const noOptionsMessage = useMemo(() => {
      if (customNotFound) return <span>{customNotFound}</span>;

      if (itemOptions && !noNotFound) {
        return (
          <span className={styles.NotFounded}>
            Если не нашли свой город в списке,
            <br />
            но точно знаете,
            <br />
            что в нём есть центр крови,
            <br />
            напишите нам на <br />
            <a href="mailto:support@donorsearch.org">support@donorsearch.org</a>
          </span>
        );
      } else {
        return <span>Поиск...</span>;
      }
    }, [itemOptions, noNotFound, customNotFound]);

    const onInputChangeLocal = (v: string) => {
      if (typeof onInputChange === "function") onInputChange(v);
    };

    const onSelectItem = (item: TListPickerItem) => {
      onSelect(item);
    };

    const loadOptions = async (e: string) => {
      if (typeof getUpdatedItems === "function") return getUpdatedItems(e);
      else {
        return itemOptions.filter((item) =>
          item.label?.toLowerCase().includes(e?.toLowerCase())
        );
      }
    };

    const onMenuOpen = () => {
      setMenuOpened(true);
      loadOptions(undefined);
      if (
        typeof window !== "undefined" &&
        placeholder &&
        isMobile &&
        !scrolled
      ) {
        const to = document?.getElementById(placeholder)?.offsetTop;
        if (to) {
          window.scrollTo({
            top: to - 50,
            behavior: "smooth",
          });
          setScrolled(true);
        }
      }
    };

    const onMenuClose = () => {
      setScrolled(false);
      setMenuOpened(false);
    };

    const ClearIndicator = ({ ...p }) => {
      const onClick = () => {
        p.setValue(undefined);
        setMenuOpened(false);
        selectRef.current.select.select.blur();
        selectRef.current.select.select.inputRef.blur();
        selectRef.current.select.select.controlRef.blur();
      };
      return (
        <img
          className={styles.ClearIndicator}
          {...p.innerProps}
          width={16}
          height={16}
          onClick={onClick}
          src="/static/images/icons/close.svg"
          alt="Close"
        />
      );
    };

    return (
      <div className={styles.ListPicker} id={placeholder}>
        <AsyncSelect
          ref={selectRef}
          placeholder={placeholder}
          cacheOptions={cacheOptions}
          value={menuIsOpen ? null : selectedValue}
          loadOptions={loadOptions}
          defaultOptions={itemOptions}
          hideSelectedOptions
          onChange={onSelectItem}
          isClearable={isClearable}
          onFocus={() => {}}
          onMenuOpen={onMenuOpen}
          onMenuClose={onMenuClose}
          isDisabled={disabled}
          styles={SearchStyle}
          loadingMessage={() => "Поиск..."}
          onInputChange={onInputChangeLocal}
          blurInputOnSelect={true}
          openMenuOnFocus={true}
          components={{
            ClearIndicator,
          }}
          noOptionsMessage={() => noOptionsMessage}
        />
        {error && <div className={styles.ListPicker_Error}>{error}</div>}
      </div>
    );
  }
);

export default ListPicker;
